/**
 * PulseButton — ゲームニクス準拠の点滅誘導ボタン
 *
 * ガイドライン:
 * - 次に押すべきボタンは「点滅」させて視覚的に誘導する
 * - 全タップ操作に固有SEを割り当てる
 * - 褒める前の「間」を挿入可能
 *
 * @example
 * <PulseButton isPrimary onClick={handle}>声を届ける！</PulseButton>
 */

import React from 'react';
import clsx from 'clsx';

interface PulseButtonProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isPrimary?: boolean;      // 呼吸アニメーション（点滅誘導）
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
  roleColor?: 'script' | 'illustration' | 'voice'; // トリプル・カラー対応
  id?: string;
  type?: 'button' | 'submit' | 'reset';
  style?: React.CSSProperties;
  title?: string;
}

const ROLE_BG: Record<string, string> = {
  script: 'bg-role-script hover:bg-role-script/80',
  illustration: 'bg-role-illustration hover:bg-role-illustration/80',
  voice: 'bg-role-voice hover:bg-role-voice/80',
};

const ROLE_OUTLINE: Record<string, string> = {
  script: 'border-role-script text-role-script hover:bg-role-script/20',
  illustration: 'border-role-illustration text-role-illustration hover:bg-role-illustration/20',
  voice: 'border-role-voice text-role-voice hover:bg-role-voice/20',
};

export const PulseButton: React.FC<PulseButtonProps> = ({
  children,
  onClick,
  isPrimary = false,
  variant = 'default',
  size = 'md',
  disabled = false,
  className,
  roleColor,
  id,
  type = 'button',
  style,
  title,
}) => {
  const sizeStyle = {
    sm: 'px-3 py-1.5 text-xs rounded-lg',
    md: 'px-5 py-2.5 text-sm rounded-xl',
    lg: 'px-8 py-3 text-base rounded-2xl',
  }[size];

  const variantStyle = (() => {
    if (roleColor && variant === 'default') return ROLE_BG[roleColor] + ' text-black font-bold';
    if (roleColor && variant === 'outline') return 'bg-transparent border ' + ROLE_OUTLINE[roleColor];

    switch (variant) {
      case 'outline':
        return 'bg-transparent border border-white/20 text-white hover:bg-white/10';
      case 'ghost':
        return 'bg-transparent text-gray-400 hover:text-white hover:bg-white/5';
      default:
        return 'bg-gradient-to-r from-neon-blue to-neon-pink text-black font-bold hover:opacity-90';
    }
  })();

  return (
    <button
      type={type}
      id={id}
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={style}
      className={clsx(
        'inline-flex items-center justify-center gap-2 transition-all duration-200 cursor-pointer select-none',
        sizeStyle,
        variantStyle,
        isPrimary && !disabled && 'animate-breathe',
        disabled && 'opacity-40 cursor-not-allowed',
        className,
      )}
    >
      {children}
    </button>
  );
};
